import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Dropdown,
  Icon,
  Input,
  Label,
  Modal,
  Pagination,
  Segment,
  Table,
} from 'semantic-ui-react';
import { showError, showSuccess, timestamp2string } from '../../helpers';
import {
  batchDeleteManagedModels,
  batchUpdateManagedModelStatus,
  deleteManagedModel,
  deleteVideoPricingRule,
  getManagedModelChanges,
  loadModelOverview,
  loadVideoPricingRules,
  saveManagedModel,
  saveVideoPricingRule,
  testManagedModelAPI,
} from './api';
import ModelEditorModal from './ModelEditorModal';
import ModelMetrics from './ModelMetrics';
import ModelTable from './ModelTable';
import './Model.css';

const emptyModel = {
  name: '',
  display_name: '',
  provider: '',
  modality: 'chat',
  tags: '',
  description: '',
  ratio: 1,
  completion_ratio: 1,
  enabled: true,
  default_params: '',
  default_system_prompt: '',
  default_max_tokens: '',
  default_temperature: '',
  default_top_p: '',
  default_top_k: '',
  default_presence_penalty: '',
  default_frequency_penalty: '',
  default_stop: '',
  default_reasoning_effort: '',
  default_response_format: '',
  default_extra_params: '',
  api_enabled: false,
  api_format: 'openai_compatible',
  api_base_url: '',
  api_key: '',
  api_key_clear: false,
  api_key_set: false,
  api_model_name: '',
  api_group: 'default',
  api_channel_id: 0,
  version: 0,
};

const statusOptions = [
  { key: 'all', value: 'all', text: '全部状态' },
  { key: 'enabled', value: 'enabled', text: '已启用' },
  { key: 'disabled', value: 'disabled', text: '已停用' },
  { key: 'managed', value: 'managed', text: '已托管' },
  { key: 'discovered', value: 'discovered', text: '自动发现' },
];

const pageSize = 50;

const emptyPricingRule = {
  id: 0,
  model_name: 'doubao-seedance-2.0',
  input_scene: 'no_video_input',
  resolution: '720p',
  token_unit_price: 92,
  reference_rmb_per_million: 92,
  resource_deduction_ratio: 1,
  enabled: true,
  effective_time: 0,
  expired_time: 0,
  remark: '',
};

const videoPricingSceneOptions = [
  { key: 'video_input', value: 'video_input', text: '含视频输入' },
  { key: 'no_video_input', value: 'no_video_input', text: '无视频输入' },
];

const videoResolutionOptions = [
  { key: '480p', value: '480p', text: '480p' },
  { key: '720p', value: '720p', text: '720p' },
  { key: '1080p', value: '1080p', text: '1080p' },
];

const actionText = {
  create: '新增',
  update: '更新',
  delete: '删除',
};

function numberOrZero(value) {
  if (value === '' || value === null || value === undefined) return 0;
  return Number(value);
}

function nullableNumber(value) {
  if (value === '' || value === null || value === undefined) return null;
  return Number(value);
}

const normalizeEditingModel = (model) => ({
  ...emptyModel,
  ...model,
  ratio: Number(model.ratio ?? 1),
  completion_ratio: Number(model.completion_ratio ?? 1),
  display_name: model.display_name || '',
  provider: model.provider || '',
  modality: model.modality || 'chat',
  tags: model.tags || '',
  description: model.description || '',
  default_params: model.default_params || '',
  default_system_prompt: model.default_system_prompt || '',
  default_max_tokens: model.default_max_tokens || '',
  default_temperature: model.default_temperature ?? '',
  default_top_p: model.default_top_p ?? '',
  default_top_k: model.default_top_k || '',
  default_presence_penalty: model.default_presence_penalty ?? '',
  default_frequency_penalty: model.default_frequency_penalty ?? '',
  default_stop: model.default_stop || '',
  default_reasoning_effort: model.default_reasoning_effort || '',
  default_response_format: model.default_response_format || '',
  default_extra_params: model.default_extra_params || '',
  api_enabled: Boolean(model.api_enabled),
  api_format: model.api_format || 'openai_compatible',
  api_base_url: model.api_base_url || '',
  api_key: '',
  api_key_clear: false,
  api_key_set: Boolean(model.api_key_set),
  api_model_name: model.api_model_name || '',
  api_group: model.api_group || 'default',
  api_channel_id: model.api_channel_id || 0,
  version: model.version || 0,
});

const buildPayload = (model) => ({
  ...model,
  name: model.name.trim(),
  ratio: Number(model.ratio || model.ratio === 0 ? model.ratio : 1),
  completion_ratio: Number(
    model.completion_ratio || model.completion_ratio === 0
      ? model.completion_ratio
      : 1
  ),
  default_max_tokens: numberOrZero(model.default_max_tokens),
  default_temperature: nullableNumber(model.default_temperature),
  default_top_p: nullableNumber(model.default_top_p),
  default_top_k: numberOrZero(model.default_top_k),
  default_presence_penalty: nullableNumber(model.default_presence_penalty),
  default_frequency_penalty: nullableNumber(model.default_frequency_penalty),
  api_enabled: Boolean(model.api_enabled),
  api_format: model.api_format || 'openai_compatible',
  api_base_url: (model.api_base_url || '').trim(),
  api_key: (model.api_key || '').trim(),
  api_key_clear: Boolean(model.api_key_clear),
  api_model_name: (model.api_model_name || '').trim(),
  api_group: (model.api_group || 'default').trim(),
});

function parseChanges(log) {
  if (!log?.changes) return [];
  try {
    const changes = JSON.parse(log.changes);
    return Array.isArray(changes) ? changes : [];
  } catch (error) {
    return [];
  }
}

function renderChangeValue(value) {
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function sceneText(scene) {
  return scene === 'video_input' ? '含视频输入' : '无视频输入';
}

function unixToDateTimeLocal(timestamp) {
  if (!timestamp) return '';
  const date = new Date(Number(timestamp) * 1000);
  const offset = date.getTimezoneOffset();
  const local = new Date(date.getTime() - offset * 60000);
  return local.toISOString().slice(0, 16);
}

function dateTimeLocalToUnix(value) {
  if (!value) return 0;
  const parsed = Date.parse(value);
  if (Number.isNaN(parsed)) return 0;
  return Math.floor(parsed / 1000);
}

const ModelManagement = () => {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState({ ...emptyModel });
  const [keyword, setKeyword] = useState('');
  const [status, setStatus] = useState('all');
  const [selectedNames, setSelectedNames] = useState([]);
  const [batchDeleting, setBatchDeleting] = useState(false);
  const [batchUpdatingStatus, setBatchUpdatingStatus] = useState(false);
  const [activePage, setActivePage] = useState(1);
  const [changeLogOpen, setChangeLogOpen] = useState(false);
  const [changeLogModel, setChangeLogModel] = useState(null);
  const [changeLogs, setChangeLogs] = useState([]);
  const [loadingChanges, setLoadingChanges] = useState(false);
  const [pricingRules, setPricingRules] = useState([]);
  const [pricingLoading, setPricingLoading] = useState(false);
  const [pricingModalOpen, setPricingModalOpen] = useState(false);
  const [editingPricing, setEditingPricing] = useState(emptyPricingRule);
  const [savingPricing, setSavingPricing] = useState(false);

  const loadOverview = async () => {
    setLoading(true);
    const [overviewRes, pricingRes] = await Promise.all([
      loadModelOverview(),
      loadVideoPricingRules(),
    ]);
    const { success, message, data } = overviewRes.data;
    if (success) setOverview(data);
    else showError(message);
    if (pricingRes.data?.success) {
      setPricingRules(pricingRes.data.data || []);
    }
    setLoading(false);
  };

  const refreshPricingRules = async () => {
    setPricingLoading(true);
    const res = await loadVideoPricingRules();
    if (res.data?.success) {
      setPricingRules(res.data.data || []);
    } else {
      showError(res.data?.message || '加载视频价格表失败');
    }
    setPricingLoading(false);
  };

  useEffect(() => {
    loadOverview().catch(showError);
  }, []);

  const filteredModels = useMemo(() => {
    const text = keyword.trim().toLowerCase();
    return (overview?.models || []).filter((model) => {
      const matchesKeyword =
        !text ||
        [model.name, model.display_name, model.provider, model.modality, model.tags]
          .filter(Boolean)
          .some((value) => value.toLowerCase().includes(text));
      const matchesStatus =
        status === 'all' ||
        (status === 'enabled' && model.enabled) ||
        (status === 'disabled' && !model.enabled) ||
        (status === 'managed' && model.managed) ||
        (status === 'discovered' && !model.managed);
      return matchesKeyword && matchesStatus;
    });
  }, [keyword, overview, status]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(filteredModels.length / pageSize)),
    [filteredModels.length]
  );

  const paginatedModels = useMemo(() => {
    const currentPage = Math.min(activePage, totalPages);
    const start = (currentPage - 1) * pageSize;
    return filteredModels.slice(start, start + pageSize);
  }, [activePage, filteredModels, totalPages]);

  const selectableNames = useMemo(
    () => filteredModels.map((model) => model.name),
    [filteredModels]
  );

  const paginatedSelectableNames = useMemo(
    () => paginatedModels.map((model) => model.name),
    [paginatedModels]
  );

  useEffect(() => {
    setSelectedNames((names) =>
      names.filter((name) => selectableNames.includes(name))
    );
  }, [selectableNames]);

  useEffect(() => {
    setActivePage(1);
  }, [keyword, status]);

  useEffect(() => {
    if (activePage > totalPages) {
      setActivePage(totalPages);
    }
  }, [activePage, totalPages]);

  const openCreate = () => {
    setEditing({ ...emptyModel });
    setModalOpen(true);
  };

  const openCreatePricing = () => {
    setEditingPricing({ ...emptyPricingRule });
    setPricingModalOpen(true);
  };

  const openEditPricing = (rule) => {
    setEditingPricing({ ...emptyPricingRule, ...rule });
    setPricingModalOpen(true);
  };

  const updatePricing = (field, value) => {
    setEditingPricing((rule) => ({ ...rule, [field]: value }));
  };

  const savePricing = async () => {
    const payload = {
      ...editingPricing,
      token_unit_price: Number(editingPricing.token_unit_price || 0),
      reference_rmb_per_million: Number(
        editingPricing.reference_rmb_per_million || 92
      ),
      resource_deduction_ratio: Number(
        editingPricing.resource_deduction_ratio || 0
      ),
      effective_time: Number(editingPricing.effective_time || 0),
      expired_time: Number(editingPricing.expired_time || 0),
      enabled: Boolean(editingPricing.enabled),
    };
    setSavingPricing(true);
    const res = await saveVideoPricingRule(payload);
    if (res.data?.success) {
      showSuccess('视频价格规则已保存');
      setPricingModalOpen(false);
      await refreshPricingRules();
    } else {
      showError(res.data?.message || '保存视频价格规则失败');
    }
    setSavingPricing(false);
  };

  const removePricing = async (rule) => {
    if (!window.confirm(`确认删除 ${rule.model_name} ${sceneText(rule.input_scene)} ${rule.resolution} 价格规则？`)) {
      return;
    }
    const res = await deleteVideoPricingRule(rule.id);
    if (res.data?.success) {
      showSuccess('视频价格规则已删除');
      await refreshPricingRules();
    } else {
      showError(res.data?.message || '删除视频价格规则失败');
    }
  };

  const openEdit = (model) => {
    setEditing(normalizeEditingModel(model));
    setModalOpen(true);
  };

  const updateEditing = (field, value) => {
    setEditing((model) => ({ ...model, [field]: value }));
  };

  const saveModel = async (model = editing, closeModal = true) => {
    if (!model.name.trim()) {
      showError('请填写模型名称');
      return;
    }
    setSaving(true);
    const res = await saveManagedModel(buildPayload(model));
    const { success, message } = res.data;
    if (success) {
      showSuccess(closeModal ? '模型配置已保存' : '模型状态已更新');
      if (closeModal) setModalOpen(false);
      await loadOverview();
    } else {
      showError(message);
    }
    setSaving(false);
  };

  const testModel = async () => {
    if (!editing.name.trim()) {
      showError('请先填写模型名称');
      return;
    }
    setTesting(true);
    const res = await testManagedModelAPI(buildPayload(editing));
    const { success, message, data } = res.data;
    if (success) {
      const latencyText = data?.latency_ms ? `，耗时 ${data.latency_ms}ms` : '';
      showSuccess(`${data?.message || '三方 API 测试通过'}${latencyText}`);
    } else {
      showError(message || data?.message || '三方 API 测试失败');
    }
    setTesting(false);
  };

  const toggleModel = async (model) => {
    await saveModel(
      {
        ...normalizeEditingModel(model),
        enabled: !model.enabled,
      },
      false
    );
  };

  const removeModel = async (model) => {
    if (
      !window.confirm(
        `确认删除模型「${model.name}」？删除后模型不会再从渠道、日志或能力表中自动恢复，重新新增同名模型可恢复。`
      )
    ) {
      return;
    }
    const res = await deleteManagedModel(model.name);
    const { success, message } = res.data;
    if (success) {
      showSuccess('模型已删除');
      await loadOverview();
    } else {
      showError(message);
    }
  };

  const openChangeLogs = async (model) => {
    setChangeLogModel(model);
    setChangeLogOpen(true);
    setLoadingChanges(true);
    const res = await getManagedModelChanges(model.name);
    const { success, message, data } = res.data;
    if (success) {
      setChangeLogs(data || []);
    } else {
      showError(message);
      setChangeLogs([]);
    }
    setLoadingChanges(false);
  };

  const toggleSelectedName = (name, checked) => {
    setSelectedNames((names) => {
      if (checked) {
        return names.includes(name) ? names : [...names, name];
      }
      return names.filter((item) => item !== name);
    });
  };

  const toggleAllVisible = (checked) => {
    setSelectedNames((names) => {
      const visibleSet = new Set(paginatedSelectableNames);
      if (checked) {
        const nextNames = [...names];
        paginatedSelectableNames.forEach((name) => {
          if (!nextNames.includes(name)) {
            nextNames.push(name);
          }
        });
        return nextNames;
      }
      return names.filter((name) => !visibleSet.has(name));
    });
  };

  const batchRemoveModels = async () => {
    if (selectedNames.length === 0) {
      showError('请选择要删除的模型');
      return;
    }
    if (
      !window.confirm(
        `确认批量删除 ${selectedNames.length} 个模型？删除后这些模型不会再自动恢复。`
      )
    ) {
      return;
    }
    setBatchDeleting(true);
    const res = await batchDeleteManagedModels(selectedNames);
    const { success, message, data } = res.data;
    if (success) {
      showSuccess(`已删除 ${data?.deleted || selectedNames.length} 个模型`);
      setSelectedNames([]);
      await loadOverview();
    } else {
      showError(message);
    }
    setBatchDeleting(false);
  };

  const batchDisableModels = async () => {
    if (selectedNames.length === 0) {
      showError('请先选择要停用的模型');
      return;
    }
    if (
      !window.confirm(
        `确认批量停用 ${selectedNames.length} 个模型？停用后这些模型将不再对外可用。`
      )
    ) {
      return;
    }
    setBatchUpdatingStatus(true);
    const res = await batchUpdateManagedModelStatus(selectedNames, false);
    const { success, message, data } = res.data;
    if (success) {
      showSuccess(`已停用 ${data?.updated || selectedNames.length} 个模型`);
      setSelectedNames([]);
      await loadOverview();
    } else {
      showError(message);
    }
    setBatchUpdatingStatus(false);
  };

  return (
    <div className='model-shell'>
      <section className='model-hero'>
        <div>
          <Label className='model-eyebrow'>模型管理</Label>
          <h1>统一维护模型目录、倍率与通路状态</h1>
          <p>
            后台集中查看所有已接入模型、计费倍率、补全倍率、可用通路和调用消耗。
            新增自定义模型后，可在渠道配置中直接选择并参与计费。
          </p>
        </div>
        <div className='model-hero-actions'>
          <Button icon labelPosition='left' onClick={loadOverview} loading={loading}>
            <Icon name='refresh' />
            刷新
          </Button>
          <Button primary icon labelPosition='left' onClick={openCreate}>
            <Icon name='plus' />
            新增模型
          </Button>
        </div>
      </section>

      <ModelMetrics overview={overview} />

      <Segment className='model-panel' loading={loading}>
        <div className='model-toolbar'>
          <div>
            <h2>模型目录</h2>
            <p>发现自渠道、能力表、日志和后台托管配置。</p>
          </div>
          <div className='model-toolbar-actions'>
            <Button
              basic
              color='orange'
              icon
              labelPosition='left'
              disabled={selectedNames.length === 0}
              loading={batchUpdatingStatus}
              onClick={batchDisableModels}
            >
              <Icon name='ban' />
              批量停用{selectedNames.length > 0 ? ` (${selectedNames.length})` : ''}
            </Button>
            <Button
              basic
              color='red'
              icon
              labelPosition='left'
              disabled={selectedNames.length === 0}
              loading={batchDeleting}
              onClick={batchRemoveModels}
            >
              <Icon name='trash alternate outline' />
              批量删除
              {selectedNames.length > 0 ? ` (${selectedNames.length})` : ''}
            </Button>
            <div className='model-filters'>
              <Input
                icon='search'
                placeholder='搜索模型、供应商、标签'
                value={keyword}
                onChange={(e, { value }) => setKeyword(value)}
              />
              <Dropdown
                selection
                options={statusOptions}
                value={status}
                onChange={(_, { value }) => setStatus(value)}
              />
            </div>
          </div>
        </div>

        <ModelTable
          models={paginatedModels}
          selectedNames={selectedNames}
          selectableNames={paginatedSelectableNames}
          onSelect={toggleSelectedName}
          onSelectAll={toggleAllVisible}
          onEdit={openEdit}
          onToggle={toggleModel}
          onDelete={removeModel}
          onChanges={openChangeLogs}
        />

        <div className='model-pagination-bar'>
          <span>
            共 {filteredModels.length} 个模型，每页 {pageSize} 个，当前第{' '}
            {Math.min(activePage, totalPages)} / {totalPages} 页
          </span>
          <Pagination
            size='mini'
            boundaryRange={1}
            siblingRange={1}
            firstItem={null}
            lastItem={null}
            prevItem={{ content: <Icon name='angle left' />, icon: true }}
            nextItem={{ content: <Icon name='angle right' />, icon: true }}
            activePage={Math.min(activePage, totalPages)}
            totalPages={totalPages}
            onPageChange={(_, { activePage: nextPage }) =>
              setActivePage(Number(nextPage) || 1)
            }
          />
        </div>
      </Segment>

      <Segment className='model-panel model-pricing-panel' loading={pricingLoading}>
        <div className='model-toolbar'>
          <div>
            <h2>视频模型价格表</h2>
            <p>按模型、创作场景、分辨率维护 Tokens 单价和资源包抵扣口径。</p>
          </div>
          <div className='model-toolbar-actions'>
            <Button icon labelPosition='left' onClick={refreshPricingRules}>
              <Icon name='refresh' />
              刷新价格
            </Button>
            <Button primary icon labelPosition='left' onClick={openCreatePricing}>
              <Icon name='plus' />
              新增价格
            </Button>
          </div>
        </div>
        <div className='model-table-wrap'>
          <Table basic='very' className='model-table'>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>模型</Table.HeaderCell>
                <Table.HeaderCell>创作场景</Table.HeaderCell>
                <Table.HeaderCell>分辨率</Table.HeaderCell>
                <Table.HeaderCell>单价</Table.HeaderCell>
                <Table.HeaderCell>基准价</Table.HeaderCell>
                <Table.HeaderCell>抵扣比例</Table.HeaderCell>
                <Table.HeaderCell>有效期</Table.HeaderCell>
                <Table.HeaderCell>状态</Table.HeaderCell>
                <Table.HeaderCell textAlign='right'>操作</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {pricingRules.map((rule) => (
                <Table.Row key={rule.id}>
                  <Table.Cell>
                    <strong>{rule.model_name}</strong>
                    {rule.remark ? <small className='model-muted'>{rule.remark}</small> : null}
                  </Table.Cell>
                  <Table.Cell>{sceneText(rule.input_scene)}</Table.Cell>
                  <Table.Cell>{rule.resolution}</Table.Cell>
                  <Table.Cell>¥{Number(rule.token_unit_price || 0).toFixed(2)} / 百万 Tokens</Table.Cell>
                  <Table.Cell>¥{Number(rule.reference_rmb_per_million || 0).toFixed(2)}</Table.Cell>
                  <Table.Cell>1:{Number(rule.resource_deduction_ratio || 0).toFixed(5)}</Table.Cell>
                  <Table.Cell>
                    <span>{rule.effective_time ? timestamp2string(rule.effective_time) : '立即'}</span>
                    <br />
                    <span className='model-muted'>{rule.expired_time ? timestamp2string(rule.expired_time) : '长期有效'}</span>
                  </Table.Cell>
                  <Table.Cell>
                    <Label color={rule.enabled ? 'green' : 'grey'}>
                      {rule.enabled ? '启用' : '停用'}
                    </Label>
                  </Table.Cell>
                  <Table.Cell textAlign='right'>
                    <Button size='mini' basic icon onClick={() => openEditPricing(rule)}>
                      <Icon name='edit outline' />
                    </Button>
                    <Button size='mini' basic color='red' icon onClick={() => removePricing(rule)}>
                      <Icon name='trash alternate outline' />
                    </Button>
                  </Table.Cell>
                </Table.Row>
              ))}
              {!pricingRules.length ? (
                <Table.Row>
                  <Table.Cell colSpan='9'>
                    <div className='model-empty'>暂无视频价格规则</div>
                  </Table.Cell>
                </Table.Row>
              ) : null}
            </Table.Body>
          </Table>
        </div>
      </Segment>

      <ModelEditorModal
        open={modalOpen}
        editing={editing}
        saving={saving}
        testing={testing}
        onClose={() => setModalOpen(false)}
        onSave={() => saveModel()}
        onTest={testModel}
        updateEditing={updateEditing}
      />

      <Modal
        open={pricingModalOpen}
        onClose={() => setPricingModalOpen(false)}
        size='small'
        closeIcon
      >
        <Modal.Header>{editingPricing.id ? '编辑视频价格' : '新增视频价格'}</Modal.Header>
        <Modal.Content>
          <div className='model-pricing-form'>
            <Input
              fluid
              label='模型'
              value={editingPricing.model_name}
              onChange={(e, { value }) => updatePricing('model_name', value)}
            />
            <Dropdown
              fluid
              selection
              options={videoPricingSceneOptions}
              value={editingPricing.input_scene}
              onChange={(_, { value }) => updatePricing('input_scene', value)}
            />
            <Dropdown
              fluid
              selection
              options={videoResolutionOptions}
              value={editingPricing.resolution}
              onChange={(_, { value }) => updatePricing('resolution', value)}
            />
            <Input
              fluid
              label='单价'
              type='number'
              value={editingPricing.token_unit_price}
              onChange={(e, { value }) => updatePricing('token_unit_price', value)}
            />
            <Input
              fluid
              label='基准价'
              type='number'
              value={editingPricing.reference_rmb_per_million}
              onChange={(e, { value }) => updatePricing('reference_rmb_per_million', value)}
            />
            <Input
              fluid
              label='抵扣比例'
              type='number'
              step='0.00001'
              value={editingPricing.resource_deduction_ratio}
              onChange={(e, { value }) => updatePricing('resource_deduction_ratio', value)}
            />
            <Input
              fluid
              label='生效时间'
              type='datetime-local'
              value={unixToDateTimeLocal(editingPricing.effective_time)}
              onChange={(e, { value }) => updatePricing('effective_time', dateTimeLocalToUnix(value))}
            />
            <Input
              fluid
              label='失效时间'
              type='datetime-local'
              value={unixToDateTimeLocal(editingPricing.expired_time)}
              onChange={(e, { value }) => updatePricing('expired_time', dateTimeLocalToUnix(value))}
            />
            <Input
              fluid
              label='备注'
              value={editingPricing.remark || ''}
              onChange={(e, { value }) => updatePricing('remark', value)}
            />
            <Button
              type='button'
              basic={!editingPricing.enabled}
              color={editingPricing.enabled ? 'green' : 'grey'}
              onClick={() => updatePricing('enabled', !editingPricing.enabled)}
            >
              <Icon name={editingPricing.enabled ? 'check circle' : 'pause circle'} />
              {editingPricing.enabled ? '启用中' : '已停用'}
            </Button>
          </div>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setPricingModalOpen(false)}>取消</Button>
          <Button primary loading={savingPricing} onClick={savePricing}>
            保存价格
          </Button>
        </Modal.Actions>
      </Modal>

      <Modal
        open={changeLogOpen}
        onClose={() => setChangeLogOpen(false)}
        size='large'
        closeIcon
      >
        <Modal.Header>
          变更记录
          {changeLogModel?.name ? ` - ${changeLogModel.name}` : ''}
        </Modal.Header>
        <Modal.Content scrolling>
          <Segment basic loading={loadingChanges} className='model-change-log-panel'>
            {changeLogs.length === 0 && !loadingChanges ? (
              <div className='model-empty'>暂无变更记录</div>
            ) : null}
            {changeLogs.map((log) => {
              const changes = parseChanges(log);
              return (
                <div className='model-change-log' key={log.id}>
                  <div className='model-change-log-head'>
                    <div>
                      <Label basic color={log.action === 'delete' ? 'red' : 'blue'}>
                        {actionText[log.action] || log.action}
                      </Label>
                      <strong>v{log.version || '-'}</strong>
                      <span>{log.operator || 'system'}</span>
                    </div>
                    <time>{timestamp2string(log.created_time)}</time>
                  </div>
                  {changes.length > 0 ? (
                    <Table basic='very' compact className='model-change-table'>
                      <Table.Header>
                        <Table.Row>
                          <Table.HeaderCell>字段</Table.HeaderCell>
                          <Table.HeaderCell>变更前</Table.HeaderCell>
                          <Table.HeaderCell>变更后</Table.HeaderCell>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {changes.map((change) => (
                          <Table.Row key={`${log.id}-${change.field}`}>
                            <Table.Cell>{change.field}</Table.Cell>
                            <Table.Cell>
                              <code>{renderChangeValue(change.before)}</code>
                            </Table.Cell>
                            <Table.Cell>
                              <code>{renderChangeValue(change.after)}</code>
                            </Table.Cell>
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table>
                  ) : (
                    <div className='model-change-empty'>无字段差异</div>
                  )}
                </div>
              );
            })}
          </Segment>
        </Modal.Content>
      </Modal>
    </div>
  );
};

export default ModelManagement;
